import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TimeSlot } from "@/models/Schedule";
import { WorkoutType } from "@/models/WorkoutType";
import { EffectiveBooking, useCreateSessionConsumptionFromRecurring, useRemoveRecurringBookingSession } from "@/hooks/useRecurringBookings";
import { useUpdateBookingStatus, useRemoveClientFromBooking } from "@/hooks/useBookings";
import { useTrialClients } from "@/hooks/useTrialClients";
import { useRevertSessionConsumption } from "@/hooks/sessions";
import { UserMinus, CheckCircle, XCircle, Repeat, AlertTriangle, CreditCard, AlertCircle, UserCheck, Undo2 } from "lucide-react";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { hasUnpaidSessions, hasLowSessions } from "@/utils/clientFilters";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router";
interface EffectiveViewClientsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeSlot: TimeSlot | null;
  selectedDate: Date;
  workoutType: WorkoutType | undefined;
  effectiveBookings: EffectiveBooking[];
}
const EffectiveViewClientsDialog = ({
  open,
  onOpenChange,
  timeSlot,
  selectedDate,
  workoutType,
  effectiveBookings
}: EffectiveViewClientsDialogProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  const [removeDialog, setRemoveDialog] = useState<{
    booking: EffectiveBooking;
    type: 'remove' | 'complete' | 'miss' | 'revert';
  } | null>(null);
  const [recurringRemoveDialog, setRecurringRemoveDialog] = useState<EffectiveBooking | null>(null);
  const [trialClientRemoveDialog, setTrialClientRemoveDialog] = useState<{
    trialClientId: string;
    trialClientName: string;
  } | null>(null);
  const [bulkCompleteDialog, setBulkCompleteDialog] = useState(false);
  
  const updateBookingStatusMutation = useUpdateBookingStatus();
  const removeClientFromBookingMutation = useRemoveClientFromBooking();
  const createSessionConsumptionFromRecurringMutation = useCreateSessionConsumptionFromRecurring();
  const removeRecurringBookingSessionMutation = useRemoveRecurringBookingSession();
  const revertSessionConsumptionMutation = useRevertSessionConsumption();
  const { removeTrialClient, isRemovingTrialClient } = useTrialClients();

  // Hook to fetch client data for low sessions check
  const {
    currentOrganization
  } = useAuth();
  const {
    data: clientsData
  } = useQuery({
    queryKey: ['clients-with-subscriptions', effectiveBookings.map(b => b.client_id)],
    queryFn: async () => {
      if (!currentOrganization || effectiveBookings.length === 0) return {};
      const clientIds = [...new Set(effectiveBookings.map(b => b.client_id))];
      const clientsMap: Record<string, {
        hasLowSessions: boolean;
      }> = {};

      // Fetch subscription data for each client
      for (const clientId of clientIds) {
        const {
          data: subscriptions
        } = await supabase.from('client_subscriptions').select('sessions_per_month, current_cycle_sessions_used, is_active').eq('client_id', clientId).eq('organization_id', currentOrganization.id).eq('is_active', true);
        if (subscriptions) {
          // Check if any subscription has low sessions
          const hasLowSessions = subscriptions.some(sub => sub.current_cycle_sessions_used >= sub.sessions_per_month - 1);
          clientsMap[clientId] = {
            hasLowSessions
          };
        }
      }
      return clientsMap;
    },
    enabled: !!currentOrganization && effectiveBookings.length > 0
  });
  const handleAction = (booking: EffectiveBooking, action: 'remove' | 'complete' | 'miss' | 'revert') => {
    if (booking.booking_type === 'recurring' && action === 'remove') {
      setRecurringRemoveDialog(booking);
    } else {
      setRemoveDialog({
        booking,
        type: action
      });
    }
  };
  const handleConfirmAction = async () => {
    if (!removeDialog) return;
    const {
      booking,
      type
    } = removeDialog;
    try {
      if (type === 'revert') {
        // Handle revert action for both regular and recurring bookings
        if (booking.session_consumption_id) {
          await revertSessionConsumptionMutation.mutateAsync({
            consumptionId: booking.session_consumption_id
          });
        } else if (booking.booking_id) {
          await revertSessionConsumptionMutation.mutateAsync({
            bookingId: booking.booking_id
          });
        }
      } else if (booking.booking_type === 'regular' && booking.booking_id) {
        if (type === 'remove') {
          await removeClientFromBookingMutation.mutateAsync(booking.booking_id);
        } else {
          await updateBookingStatusMutation.mutateAsync({
            bookingId: booking.booking_id,
            status: type === 'complete' ? 'completed' : 'missed'
          });
        }
      } else if (booking.booking_type === 'recurring' && booking.recurring_booking_id) {
        if (type !== 'remove') {
          await createSessionConsumptionFromRecurringMutation.mutateAsync({
            recurringBookingId: booking.recurring_booking_id,
            consumptionDate: selectedDate,
            consumptionType: type === 'complete' ? 'completed' : 'missed'
          });
        }
      }
      setRemoveDialog(null);

      // Keep dialog open for faster actions - user can close manually
    } catch (error) {
      // Error handling is done in the mutations
    }
  };
  const handleRecurringRemove = async (mode: 'single' | 'fromDate' | 'all') => {
    if (!recurringRemoveDialog?.recurring_booking_id) return;
    try {
      await removeRecurringBookingSessionMutation.mutateAsync({
        recurringBookingId: recurringRemoveDialog.recurring_booking_id,
        sessionDate: selectedDate,
        removeAll: mode === 'all',
        removeFromDate: mode === 'fromDate',
      });
      setRecurringRemoveDialog(null);

      // Keep dialog open for faster actions - user can close manually
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleTrialClientRemove = async () => {
    if (!trialClientRemoveDialog) return;
    
    try {
      await removeTrialClient(trialClientRemoveDialog.trialClientId);
      setTrialClientRemoveDialog(null);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleBulkComplete = async () => {
    setBulkCompleteDialog(false);
    
    // Filter only unprocessed regular bookings (not trial clients)
    const unprocessedBookings = effectiveBookings.filter(booking => {
      const isTrialClient = booking.client_payment_status === 'trial';
      const effectiveStatus = booking.session_consumption_type || booking.booking_status;
      const isProcessed = effectiveStatus === 'completed' || effectiveStatus === 'missed';
      return !isTrialClient && !isProcessed;
    });

    try {
      // Process all bookings in parallel
      const promises = unprocessedBookings.map(async (booking) => {
        if (booking.booking_type === 'regular' && booking.booking_id) {
          return updateBookingStatusMutation.mutateAsync({
            bookingId: booking.booking_id,
            status: 'completed'
          });
        } else if (booking.booking_type === 'recurring' && booking.recurring_booking_id) {
          return createSessionConsumptionFromRecurringMutation.mutateAsync({
            recurringBookingId: booking.recurring_booking_id,
            consumptionDate: selectedDate,
            consumptionType: 'completed'
          });
        }
      });

      await Promise.all(promises);
    } catch (error) {
      // Error handling is done in the mutations
    }
  };
  const content = <div className={isFullScreen ? "flex-1 p-6 flex flex-col overflow-hidden" : "p-6 flex flex-col overflow-hidden"}>
      {timeSlot && <div className={isFullScreen ? "flex flex-col overflow-hidden h-full" : "flex flex-col overflow-hidden h-full"}>
          <div className="text-sm space-y-1 flex-shrink-0 mb-4">
            <div><strong>{t('common.date')}:</strong> {selectedDate.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</div>
            <div><strong>{t('common.time')}:</strong> {timeSlot.startTime} - {timeSlot.endTime}</div>
            {workoutType && <div className="flex items-center gap-2">
                <strong>{t('calendar.workoutType')}:</strong>
                <Badge variant="secondary" style={{
            backgroundColor: workoutType.color,
            color: 'white'
          }}>
                  {workoutType.name}
                </Badge>
              </div>}
          </div>
          
          {/* Bulk actions */}
          {effectiveBookings.some(booking => {
            const isTrialClient = booking.client_payment_status === 'trial';
            const effectiveStatus = booking.session_consumption_type || booking.booking_status;
            const isProcessed = effectiveStatus === 'completed' || effectiveStatus === 'missed';
            return !isTrialClient && !isProcessed;
          }) && (
            <div className="flex-shrink-0 mb-4">
              <Button
                onClick={() => setBulkCompleteDialog(true)}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                disabled={updateBookingStatusMutation.isPending || createSessionConsumptionFromRecurringMutation.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('calendar.bulkCompleteAll')}
              </Button>
            </div>
          )}
          
          <div className={isFullScreen ? "flex-1 min-h-0" : "flex-1 min-h-0 overflow-auto"}>
            <ScrollArea className="h-full">
              <div className="space-y-3 pr-4">
              {effectiveBookings.length === 0 ? <p className="text-center text-muted-foreground py-4">
                  {t('calendar.noClientsBooked')}
                </p> : effectiveBookings.map((booking, index) => {
                // Check if this is a trial client booking using the new payment_status field
                const isTrialClient = booking.client_payment_status === 'trial';
                
                if (isTrialClient) {
                  // Render trial clients
                  return (
                    <div key={`trial-${booking.booking_id}-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-600">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button 
                            className="font-medium hover:text-primary transition-colors cursor-pointer text-left"
                            onClick={() => booking.client_id && navigate(`/client/${booking.client_id}`)}
                          >
                            {booking.client_first_name}
                          </button>
                          
                          {/* Trial client badge with icon */}
                          <Badge variant="outline" className="text-xs flex items-center gap-1 border-blue-300 text-blue-700 bg-blue-50 dark:border-blue-600 dark:text-blue-300 dark:bg-blue-900/20">
                            <UserCheck className="h-3 w-3" />
                            {t('calendar.trialClient')}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {t('calendar.trialClientNote')}
                        </div>
                      </div>
                        
                      <div className="flex items-center gap-1">
                        {/* Only remove button for trial clients - no complete/miss buttons */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            // For trial clients, extract the actual trial client ID from the trial_clients array
                            const trialClient = booking.trial_clients?.[0];
                            if (trialClient) {
                              setTrialClientRemoveDialog({
                                trialClientId: trialClient.id,
                                trialClientName: trialClient.full_name
                              });
                            }
                          }}
                          className="text-red-600 hover:text-red-700" 
                          disabled={isRemovingTrialClient}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                } else {
                  // Render regular clients
                  const effectiveStatus = booking.session_consumption_type || booking.booking_status;
                  const isCompleted = effectiveStatus === 'completed';
                  const isMissed = effectiveStatus === 'missed';
                  const isProcessed = isCompleted || isMissed;
                  
                  return (
                    <div key={`${booking.client_id}-${booking.booking_type}-${index}`} className={`flex items-center justify-between p-3 rounded-lg ${isCompleted ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-600' : isMissed ? 'bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-600' : 'bg-muted/30'}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button 
                            className="font-medium hover:text-primary transition-colors cursor-pointer text-left"
                            onClick={() => navigate(`/client/${booking.client_id}`)}
                          >
                            {booking.client_first_name} {booking.client_last_name}
                          </button>
                          
                          {/* Client indicators */}
                          {booking.client_payment_status && hasUnpaidSessions({
                            paymentStatus: booking.client_payment_status
                          } as any) && <Badge variant="destructive" className="text-xs flex items-center gap-1">
                              <CreditCard className="h-3 w-3" />
                              {t('clients.status.unpaidSessions')}
                            </Badge>}
                          
                          {/* Low sessions indicator */}
                          {clientsData?.[booking.client_id]?.hasLowSessions && <Badge variant="outline" className="text-xs flex items-center gap-1 border-orange-300 text-orange-700 bg-orange-50 dark:border-orange-600 dark:text-orange-300 dark:bg-orange-900/20">
                              <AlertCircle className="h-3 w-3" />
                              {t('clients.status.lowSessions')}
                            </Badge>}
                          
                          {/* Booking type badges */}
                          {booking.booking_type === 'recurring' && <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <Repeat className="h-3 w-3" />
                              {t('clients.status.recurring')}
                            </Badge>}
                          
                          {/* Status badges */}
                          {isCompleted && <Badge variant="completed" className="text-xs">
                              {t('clients.status.completed')}
                            </Badge>}
                          {isMissed && <Badge variant="missed" className="text-xs">
                              {t('clients.status.missed')}
                            </Badge>}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {booking.client_phone && (
                            <a 
                              href={`tel:${booking.client_phone}`} 
                              className="hover:text-primary transition-colors"
                            >
                              {booking.client_phone}
                            </a>
                          )}
                          {booking.client_email && ` • ${booking.client_email}`}
                        </div>
                      </div>
                        
                      <div className="flex items-center gap-1">
                        {/* Show complete/miss buttons only for unprocessed regular bookings */}
                        {!isProcessed && <>
                            <Button variant="outline" size="sm" onClick={() => handleAction(booking, 'complete')} className="text-green-600 hover:text-green-700" disabled={updateBookingStatusMutation.isPending || createSessionConsumptionFromRecurringMutation.isPending}>
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleAction(booking, 'miss')} className="text-orange-600 hover:text-orange-700" disabled={updateBookingStatusMutation.isPending || createSessionConsumptionFromRecurringMutation.isPending}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>}
                        {/* Show revert button for processed bookings */}
                        {isProcessed && <Button variant="outline" size="sm" onClick={() => handleAction(booking, 'revert')} className="text-blue-600 hover:text-blue-700" disabled={revertSessionConsumptionMutation.isPending} title={t('sessions.revert')}>
                            <Undo2 className="h-4 w-4" />
                          </Button>}
                        {/* Always show remove button */}
                        <Button variant="outline" size="sm" onClick={() => handleAction(booking, 'remove')} className="text-red-600 hover:text-red-700" disabled={removeClientFromBookingMutation.isPending || removeRecurringBookingSessionMutation.isPending}>
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                }
              }).flat()}
              </div>
            </ScrollArea>
          </div>
        </div>}
    </div>;
  const DialogComponent = isFullScreen ? Sheet : Dialog;
  const DialogContentComponent = isFullScreen ? SheetContent : DialogContent;
  const DialogHeaderComponent = isFullScreen ? SheetHeader : DialogHeader;
  const DialogTitleComponent = isFullScreen ? SheetTitle : DialogTitle;
  return <>
      <DialogComponent open={open} onOpenChange={onOpenChange}>
        <DialogContentComponent {...isFullScreen ? {
        side: "bottom"
      } : {}} className={isFullScreen ? "h-screen w-full rounded-none border-0 p-0 flex flex-col pt-safe-top pb-safe-bottom" : "max-w-lg max-h-[80vh] flex flex-col"}>
          <DialogHeaderComponent className={isFullScreen ? "p-6 border-b flex-shrink-0 flex items-center justify-between min-h-[4rem]" : "flex-shrink-0"}>
            <DialogTitleComponent className={isFullScreen ? "text-lg font-semibold" : ""}>{t('calendar.viewClientsForSession')}</DialogTitleComponent>
          </DialogHeaderComponent>
          {content}
        </DialogContentComponent>
      </DialogComponent>

      {/* Regular confirmation dialog */}
      {isFullScreen ? <Sheet open={removeDialog !== null} onOpenChange={() => setRemoveDialog(null)}>
          <SheetContent side="bottom" className="h-auto min-h-[40vh] rounded-t-lg border-0 p-0 flex flex-col">
            <SheetHeader className="p-6 border-b flex-shrink-0">
              <SheetTitle className="text-lg font-semibold text-left">
                {removeDialog?.type === 'complete' && t('calendar.confirmMarkCompleted')}
                {removeDialog?.type === 'miss' && t('calendar.confirmMarkAbsent')}
                {removeDialog?.type === 'remove' && t('calendar.confirmRemoveClient')}
                {removeDialog?.type === 'revert' && t('sessions.confirmRevert')}
              </SheetTitle>
              <div className="text-sm text-muted-foreground text-left mt-2">
                {removeDialog?.type === 'complete' && t('calendar.confirmMarkCompletedDescription', {
              clientName: `${removeDialog.booking.client_first_name} ${removeDialog.booking.client_last_name}`
            })}
                {removeDialog?.type === 'miss' && t('calendar.confirmMarkAbsentDescription', {
              clientName: `${removeDialog.booking.client_first_name} ${removeDialog.booking.client_last_name}`
            })}
                {removeDialog?.type === 'remove' && t('calendar.confirmRemoveClientDescription', {
              clientName: `${removeDialog.booking.client_first_name} ${removeDialog.booking.client_last_name}`
            })}
                {removeDialog?.type === 'revert' && t('sessions.confirmRevertDescription')}
              </div>
            </SheetHeader>
            <div className="p-6 flex flex-col gap-3">
              <Button onClick={handleConfirmAction} className="w-full">
                {t('common.confirm')}
              </Button>
              <Button variant="outline" onClick={() => setRemoveDialog(null)} className="w-full">
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet> : <AlertDialog open={removeDialog !== null} onOpenChange={() => setRemoveDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {removeDialog?.type === 'complete' && t('calendar.confirmMarkCompleted')}
                {removeDialog?.type === 'miss' && t('calendar.confirmMarkAbsent')}
                {removeDialog?.type === 'remove' && t('calendar.confirmRemoveClient')}
                {removeDialog?.type === 'revert' && t('sessions.confirmRevert')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {removeDialog?.type === 'complete' && t('calendar.confirmMarkCompletedDescription', {
              clientName: `${removeDialog.booking.client_first_name} ${removeDialog.booking.client_last_name}`
            })}
                {removeDialog?.type === 'miss' && t('calendar.confirmMarkAbsentDescription', {
              clientName: `${removeDialog.booking.client_first_name} ${removeDialog.booking.client_last_name}`
            })}
                {removeDialog?.type === 'remove' && t('calendar.confirmRemoveClientDescription', {
              clientName: `${removeDialog.booking.client_first_name} ${removeDialog.booking.client_last_name}`
            })}
                {removeDialog?.type === 'revert' && t('sessions.confirmRevertDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmAction}>
                {t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>}

      {/* Recurring removal dialog */}
      {isFullScreen ? <Sheet open={recurringRemoveDialog !== null} onOpenChange={() => setRecurringRemoveDialog(null)}>
          <SheetContent side="bottom" className="h-auto min-h-[40vh] rounded-t-lg border-0 p-0 flex flex-col">
            <SheetHeader className="p-6 border-b flex-shrink-0">
              <SheetTitle className="text-lg font-semibold text-left">{t('calendar.confirmRemoveRecurring')}</SheetTitle>
              <div className="text-sm text-muted-foreground text-left mt-2">
                {recurringRemoveDialog && t('calendar.confirmRemoveClientDescription', { clientName: `${recurringRemoveDialog.client_first_name} ${recurringRemoveDialog.client_last_name}` })}
              </div>
            </SheetHeader>
            <div className="p-6 flex flex-col gap-3">
              <Button onClick={() => handleRecurringRemove('single')} className="w-full">
                {t('calendar.removeThisSession')}
              </Button>
              <Button onClick={() => handleRecurringRemove('fromDate')} variant="secondary" className="w-full">
                {t('calendar.removeFromDateOnwards')}
              </Button>
              <Button onClick={() => handleRecurringRemove('all')} variant="destructive" className="w-full px-[8px]">
                {t('calendar.removeAllRecurring')}
              </Button>
              <Button variant="outline" onClick={() => setRecurringRemoveDialog(null)} className="w-full">
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet> : <AlertDialog open={recurringRemoveDialog !== null} onOpenChange={() => setRecurringRemoveDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('calendar.confirmRemoveRecurring')}</AlertDialogTitle>
              <AlertDialogDescription>
                {recurringRemoveDialog && t('calendar.confirmRemoveClientDescription', { clientName: `${recurringRemoveDialog.client_first_name} ${recurringRemoveDialog.client_last_name}` })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-3 sm:flex-col">
              <AlertDialogAction onClick={() => handleRecurringRemove('single')} className="w-full">
                {t('calendar.removeThisSession')}
              </AlertDialogAction>
              <AlertDialogAction onClick={() => handleRecurringRemove('fromDate')} className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80">
                {t('calendar.removeFromDateOnwards')}
              </AlertDialogAction>
              <AlertDialogAction onClick={() => handleRecurringRemove('all')} className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {t('calendar.removeAllRecurring')}
              </AlertDialogAction>
              <AlertDialogCancel className="w-full">{t('common.cancel')}</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>}

      {/* Trial client removal dialog */}
      {isFullScreen ? (
        <Sheet open={trialClientRemoveDialog !== null} onOpenChange={() => setTrialClientRemoveDialog(null)}>
          <SheetContent side="bottom" className="h-auto min-h-[40vh] rounded-t-lg border-0 p-0 flex flex-col">
            <SheetHeader className="p-6 border-b flex-shrink-0">
              <SheetTitle className="text-lg font-semibold text-left">
                {t('calendar.confirmRemoveClient')}
              </SheetTitle>
              <div className="text-sm text-muted-foreground text-left mt-2">
                {trialClientRemoveDialog && t('calendar.confirmRemoveClientDescription', {
                  clientName: trialClientRemoveDialog.trialClientName
                })}
              </div>
            </SheetHeader>
            <div className="p-6 flex flex-col gap-3">
              <Button onClick={handleTrialClientRemove} className="w-full">
                {t('common.confirm')}
              </Button>
              <Button variant="outline" onClick={() => setTrialClientRemoveDialog(null)} className="w-full">
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <AlertDialog open={trialClientRemoveDialog !== null} onOpenChange={() => setTrialClientRemoveDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('calendar.confirmRemoveClient')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {trialClientRemoveDialog && t('calendar.confirmRemoveClientDescription', {
                  clientName: trialClientRemoveDialog.trialClientName
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleTrialClientRemove}>
                {t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Bulk complete confirmation dialog */}
      {isFullScreen ? (
        <Sheet open={bulkCompleteDialog} onOpenChange={setBulkCompleteDialog}>
          <SheetContent side="bottom" className="h-auto min-h-[40vh] rounded-t-lg border-0 p-0 flex flex-col">
            <SheetHeader className="p-6 border-b flex-shrink-0">
              <SheetTitle className="text-lg font-semibold text-left">
                {t('calendar.confirmBulkComplete')}
              </SheetTitle>
              <div className="text-sm text-muted-foreground text-left mt-2">
                {t('calendar.confirmBulkCompleteDescription')}
              </div>
            </SheetHeader>
            <div className="p-6 flex flex-col gap-3">
              <Button onClick={handleBulkComplete} className="w-full bg-green-600 hover:bg-green-700 text-white">
                {t('common.confirm')}
              </Button>
              <Button variant="outline" onClick={() => setBulkCompleteDialog(false)} className="w-full">
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <AlertDialog open={bulkCompleteDialog} onOpenChange={setBulkCompleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('calendar.confirmBulkComplete')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('calendar.confirmBulkCompleteDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleBulkComplete} className="bg-green-600 hover:bg-green-700 text-white">
                {t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>;
};
export default EffectiveViewClientsDialog;